import { add, eq, gt, gte, isZero, lt, lte, toDouble, ZERO, type Decimal } from '../core/decimal.ts';
import type { Day } from '../core/calendar.ts';
import type { BalanceSnapshot, Bucket, CalcContext, Goal, GoalOperator, GoalSeries, Series } from '../types/domain.ts';
import { monthsUntilMatch, type GoalDateMatch } from './goal-date-match.ts';
import { snapshotNetWorth } from '../calculations/net-worth.ts';
import { clampPercent } from './percent.ts';

/** The cumulative forecast series a goal can track (CumulativeForecast, keyed by GoalSeries). */
export type GoalForecast = { readonly [K in GoalSeries]?: Series } & {
  /** The forecast's debt-free day (R15): dates every debt-free goal. */
  readonly debtFreeDate?: Day | null;
};

/** HistoricalBaselines: historical maxima from checkpoints. */
export interface HistoricalBaselines {
  readonly maxDebt: Decimal | null;
  readonly maxNetWorth: Decimal | null;
}

export const NO_BASELINES: HistoricalBaselines = { maxDebt: null, maxNetWorth: null };

export interface GoalProgress {
  readonly goal: Goal;
  readonly currentValue: Decimal;
  readonly targetValue: Decimal;
  /** Double, 0…100. */
  readonly progressPercent: number;
  readonly estimatedDate: Day | null;
  readonly monthsRemaining: number | null;
  readonly isAchieved: boolean;
  /** For a goal with a "by" day (R26, decision 6), how its date compares with it. Not on the contract's wire. */
  readonly deadline?: GoalDeadline | null;
}

export interface GoalDeadline {
  /** The goal's "by" day. */
  readonly date: Day;
  /** Met now, or met by the end of the by day's month. */
  readonly onTrack: boolean;
  /** Whole calendar months from the by month to the goal's month; 0 on track; null when not within the horizon. */
  readonly monthsBehind: number | null;
  /** Whole calendar months from the goal's month (this month once met) to the by month; 0 when not ahead. */
  readonly monthsAhead: number;
  /** The goal's series at the end of the by month (now, when that month has passed). */
  readonly valueAtDate: Decimal;
  /** How far `valueAtDate` is from the target, 0 when on track. */
  readonly shortfall: Decimal;
}

// MARK: Matching

/** Whether a single value already satisfies the goal's operation. */
function meetsTarget(operation: GoalOperator, value: Decimal, target: Decimal): boolean {
  switch (operation) {
    case 'crossover':
      return gte(value, target);
    case 'crossunder':
      return lte(value, target);
    case 'equal':
      return eq(value, target);
  }
}

/** evaluateGoal (R16): whether moving from `previous` to `current` reaches or passes the target. */
function crossesTarget(operation: GoalOperator, previous: Decimal, current: Decimal, target: Decimal): boolean {
  const crossedOver = gte(current, target) && lt(previous, target);
  const crossedUnder = lte(current, target) && gt(previous, target);
  const hitExact = eq(current, target) && !eq(previous, target);
  switch (operation) {
    case 'crossover':
      return crossedOver;
    case 'crossunder':
      return crossedUnder;
    case 'equal':
      return hitExact || crossedOver || crossedUnder;
  }
}

/**
 * Crossunder: once the series has been above the target, the START of the first bucket back
 * at/below it (the month it happens, R16); if it never exceeds the target, the first bucket's
 * START when it is met.
 */
function crossunderDate(payments: Series, target: Decimal): Day | null {
  const everAbove = payments.some((bucket) => gt(bucket.amount, target));
  if (!everAbove) {
    const first = payments[0];
    return first !== undefined && lte(first.amount, target) ? first.start : null;
  }
  const firstAbove = payments.findIndex((bucket) => gt(bucket.amount, target));
  const cleared = payments.slice(firstAbove).find((bucket) => lte(bucket.amount, target));
  return cleared?.start ?? null;
}

/**
 * Crossover/equal: the first bucket's START when already met, else the START of the first
 * bucket that reaches the target from its predecessor (R16).
 */
function crossingDate(operation: GoalOperator, payments: readonly Bucket[], target: Decimal): Day | null {
  const first = payments[0];
  if (first === undefined) return null;
  if (meetsTarget(operation, first.amount, target)) return first.start;
  const crossing = payments.find((bucket, i) => i > 0 && crossesTarget(operation, payments[i - 1]!.amount, bucket.amount, target));
  return crossing?.start ?? null;
}

/**
 * Empty series: met "now" when the current value (0) meets the target, for every operation (R8).
 */
const emptySeriesDate = (operation: GoalOperator, target: Decimal, now: Day): Day | null =>
  operation === 'crossunder' ? (eq(target, ZERO) ? now : null) : meetsTarget(operation, ZERO, target) ? now : null;

/** GoalTracker.findMatch: when the goal is reached in `payments`; currentValue is the first bucket (0 if none). */
export function findGoalMatch(goal: Goal, payments: Series, context: CalcContext): GoalDateMatch {
  const currentValue = payments[0]?.amount ?? ZERO;
  const date =
    payments.length === 0
      ? emptySeriesDate(goal.operation, goal.targetValue, context.now)
      : goal.operation === 'crossunder'
        ? crossunderDate(payments, goal.targetValue)
        : crossingDate(goal.operation, payments, goal.targetValue);
  return { goal, date, currentValue };
}

/** A debt-free goal: the debt series falling to 0. */
const isDebtFreeGoal = (goal: Goal): boolean => goal.series === 'debt' && goal.operation === 'crossunder' && isZero(goal.targetValue);

/**
 * GoalTracker.findGoalMatches: each goal's match; a reached debt-free goal takes the forecast's
 * debt-free day when it has one (R15), the same day as metrics and the payoff dates.
 */
export const findGoalMatches = (goals: readonly Goal[], forecast: GoalForecast, context: CalcContext): readonly GoalDateMatch[] =>
  goals.map((goal) => {
    const match = findGoalMatch(goal, forecast[goal.series] ?? [], context);
    const debtFreeDay = forecast.debtFreeDate ?? null;
    return isDebtFreeGoal(goal) && match.date !== null && debtFreeDay !== null ? { ...match, date: debtFreeDay } : match;
  });

// MARK: Progress

/** getCurrentValueForSeries: a checkpoint's value for a goal series (passive income is not stored). */
export function snapshotValue(series: GoalSeries, balances: BalanceSnapshot): Decimal {
  switch (series) {
    case 'netWorth':
      return snapshotNetWorth(balances);
    case 'cash':
      return add(balances.cash, balances.savings);
    case 'investment':
      return balances.investments;
    case 'debt':
      return balances.debt;
    case 'passive':
      return ZERO;
  }
}

/**
 * calculateProgressPercent, in Double. Growth goals: current/target (0 when target is 0 or
 * current ≤ 0). Reduction goals: 100 once at/below target; otherwise progress from a baseline
 * (maxDebt for debt goals when known, else current) towards the target.
 */
export function goalProgressPercent(currentValue: Decimal, goal: Goal, baselines: HistoricalBaselines): number {
  const current = toDouble(currentValue);
  const target = toDouble(goal.targetValue);
  if (goal.operation !== 'crossunder') {
    if (target === 0 || current <= 0) return 0;
    return clampPercent((current / target) * 100);
  }
  if (current <= target) return 100;
  const baseline = goal.series === 'debt' && baselines.maxDebt !== null ? toDouble(baselines.maxDebt) : current;
  if (!(baseline > target && current <= baseline)) return 0;
  return clampPercent(((baseline - current) / (baseline - target)) * 100);
}

function progressFor(match: GoalDateMatch, currentValue: Decimal, baselines: HistoricalBaselines, context: CalcContext): GoalProgress {
  const { goal } = match;
  const isAchieved =
    meetsTarget(goal.operation, currentValue, goal.targetValue) || (match.date !== null && match.date <= context.now);
  return {
    goal,
    currentValue,
    targetValue: goal.targetValue,
    progressPercent: goalProgressPercent(currentValue, goal, baselines),
    estimatedDate: match.date,
    monthsRemaining: isAchieved ? 0 : monthsUntilMatch(match.date, context.now),
    isAchieved,
  };
}

/**
 * GoalTracker.calculateProgress. Dates come from the forecast; the current value comes from
 * the checkpoint balances when given, else from each series' first bucket.
 */
export function calculateGoalProgress(
  goals: readonly Goal[],
  forecast: GoalForecast,
  context: CalcContext,
  options: { readonly currentBalances?: BalanceSnapshot | null; readonly baselines?: HistoricalBaselines } = {},
): readonly GoalProgress[] {
  const baselines = options.baselines ?? NO_BASELINES;
  const balances = options.currentBalances ?? null;
  return findGoalMatches(goals, forecast, context).map((match) =>
    progressFor(match, balances === null ? match.currentValue : snapshotValue(match.goal.series, balances), baselines, context),
  );
}
